<script setup>
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import ProjectService from "../../services/projectService.js";
import { useProjectDocs } from "../../composables/useProjectDocs.js";
import { useProjectsList } from "../../composables/useProjectsList.js";
import { useProjectsByTab } from "../../composables/useProjectsByTab.js";
import { useProjectsPanel } from "../../composables/useProjectsPanel.js";
import { useInfiniteScroll } from "../../composables/useInfiniteScroll.js";
import { useTopBar } from "../../composables/useTopBar.js";
import { hasPermission, COLLABORATOR_PERMISSIONS } from "../../composables/usePermissions.js";
import { getFileTypeLabel, getFileTypeBadgeClass } from "../../composables/useFileType.js";
import { truncateMiddle } from "../../composables/useTextTruncate.js";
import Spinner from "../../components/ui/Spinner.vue";
import DocumentCardMenu from "../../components/ui/DocumentCardMenu.vue";
import DownloadOnlyModal from "../../components/ui/DownloadOnlyModal.vue";

const route = useRoute();
const router = useRouter();
const { set: setTopBar } = useTopBar();
const projectService = new ProjectService();
const { docs, isLoading, isLoadingMore, hasMore, loadFirstPage, loadMore, remove: removeDoc } = useProjectDocs();
const { adjustDocCount } = useProjectsList();
const { adjustDocCount: adjustTabDocCount } = useProjectsByTab();
const { setDocFavorite: setPanelDocFavorite, renameDoc: renamePanelDoc, removeDoc: removePanelDoc } = useProjectsPanel();

const project = ref(null); // full project object, incl. permissions/userRole — needed by DocumentCardMenu
const downloadOnlyDoc = ref(null); // set to open DownloadOnlyModal instead of navigating

const SORT_OPTIONS = [
  { by: "LAST_MODIFIED", label: "Last modified" },
  { by: "DATE_ADDED",    label: "Date added" },
  { by: "NAME",          label: "Name" },
];
const sortBy = ref("LAST_MODIFIED");
const sortDir = ref("DESC"); // ASC | DESC
const showSortMenu = ref(false);
const viewMode = ref("grid"); // grid | list

const sortLabel = computed(() => SORT_OPTIONS.find((o) => o.by === sortBy.value)?.label ?? "");

async function loadProjectHeader(projectId) {
  const data = await projectService.getProject({ projectId });
  project.value = data;
  setTopBar({
    title: data?.name ?? "Project",
    showMore: true,
    showShare: hasPermission(data?.permissions, COLLABORATOR_PERMISSIONS.SHARE_PROJECT),
    project: data,
  });
}

function onDocumentDeleted(doc) {
  removeDoc(doc.id);
  removePanelDoc(doc.id); // side panel's docs list is a separate cache
  adjustDocCount(route.params.projectId, -1);
  adjustTabDocCount(route.params.projectId, -1);
}

function onDocumentRenamed(doc, { name }) {
  renamePanelDoc(doc.id, name); // side panel's docs list is a separate cache
}

watch(
  () => route.params.projectId,
  (id) => {
    if (!id) return;
    loadProjectHeader(id);
    loadFirstPage(id, sortBy.value, sortDir.value);
  },
  { immediate: true }
);

// Sort is applied server-side, so changing it re-fetches from the first page
watch([sortBy, sortDir], () => loadFirstPage(route.params.projectId, sortBy.value, sortDir.value));

function setSort(by) {
  sortBy.value = by;
  showSortMenu.value = false;
}
function toggleSortDir() {
  sortDir.value = sortDir.value === "ASC" ? "DESC" : "ASC";
}

async function toggleFavorite(doc) {
  const isFavorite = !doc.isFavorite;
  doc.isFavorite = isFavorite; // optimistic
  setPanelDocFavorite(doc.id, isFavorite); // side panel's docs list is a separate cache
  try {
    await projectService.MarkDocumentFavorite({
      documentId: doc.id,
      isFavorite,
      email: localStorage.getItem("email"),
      tenantId: localStorage.getItem("tenantId"),
    });
  } catch (error) {
    doc.isFavorite = !isFavorite; // revert on failure
    setPanelDocFavorite(doc.id, !isFavorite);
  }
}

function openDocument(doc) {
  if (doc.isAnnotatable === false) {
    downloadOnlyDoc.value = doc;
    return;
  }
  router.push({ name: "v2-document", params: { projectId: route.params.projectId, docId: doc.id } });
}

function formatDate(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

function mimeBadge(doc) {
  const mime = doc.origin?.mimeType ?? doc.originMimeType ?? "";
  return { label: getFileTypeLabel(mime), class: getFileTypeBadgeClass(mime) };
}

// Some uploads can't be rendered in the annotation viewer (isAnnotatable:
// false) — the backend still stores and lets you download them, same as the
// legacy app's "Download-only Files" section beneath the normal grid.
const annotatableDocs = computed(() => docs.value.filter((d) => d.isAnnotatable !== false));
const downloadOnlyDocs = computed(() => docs.value.filter((d) => d.isAnnotatable === false));

const sentinelEl = ref(null);
useInfiniteScroll(sentinelEl, () => loadMore());
</script>

<template>
  <div data-testid="project-page" class="flex flex-col p-8 bg-slate-50 dark:bg-slate-900 min-h-full" @click="showSortMenu = false">

    <!-- Toolbar -->
    <div data-testid="project-toolbar" class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-2">
        <div class="relative" @click.stop>
          <button
            data-testid="project-sort-button"
            class="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-500 dark:text-slate-400 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            @click="showSortMenu = !showSortMenu"
          >
            Sort by <span class="font-semibold text-slate-800 dark:text-slate-100">{{ sortLabel }}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <div
            v-if="showSortMenu"
            data-testid="project-sort-menu"
            class="absolute top-full left-0 mt-2 w-44 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1.5 z-20 overflow-hidden"
          >
            <button
              v-for="opt in SORT_OPTIONS"
              :key="opt.by"
              :data-testid="`project-sort-option-${opt.by.toLowerCase().replace(/_/g, '-')}`"
              :class="sortBy === opt.by ? 'text-brand bg-orange-50 dark:bg-orange-950/30' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'"
              class="flex items-center w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors"
              @click="setSort(opt.by)"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>

        <button
          data-testid="project-sort-direction"
          class="flex items-center justify-center w-9 h-9 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          :title="sortDir === 'ASC' ? 'Ascending' : 'Descending'"
          @click="toggleSortDir"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M8 5v14M8 5L4 9M8 5l4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M16 19V5M16 19l4-4M16 19l-4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <!-- Grid / list toggle -->
      <div data-testid="project-view-toggle" class="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
        <button
          data-testid="project-view-grid"
          :class="viewMode === 'grid' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100' : 'bg-transparent text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'"
          class="flex items-center justify-center w-8 h-8 rounded-lg border-0 cursor-pointer transition-colors"
          @click="viewMode = 'grid'"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="2" />
            <rect x="13" y="3" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="2" />
            <rect x="3" y="13" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="2" />
            <rect x="13" y="13" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="2" />
          </svg>
        </button>
        <button
          data-testid="project-view-list"
          :class="viewMode === 'list' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100' : 'bg-transparent text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'"
          class="flex items-center justify-center w-8 h-8 rounded-lg border-0 cursor-pointer transition-colors"
          @click="viewMode = 'list'"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="4" width="4" height="4" rx="1" fill="currentColor" />
            <path d="M10 6h11" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <rect x="3" y="10" width="4" height="4" rx="1" fill="currentColor" />
            <path d="M10 12h11" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <rect x="3" y="16" width="4" height="4" rx="1" fill="currentColor" />
            <path d="M10 18h11" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Loading (first page) -->
    <div v-if="isLoading" data-testid="project-loading" class="flex justify-center py-20"><Spinner size="48px" /></div>

    <!-- Empty state -->
    <div v-else-if="docs.length === 0" data-testid="project-empty-state" class="flex flex-col items-center justify-center min-h-[50vh] gap-3 text-slate-400 dark:text-slate-500">
      <div class="flex items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 mb-1" style="width:72px;height:72px">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M14 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <h2 class="text-lg font-semibold text-slate-800 dark:text-slate-100 m-0">No documents yet</h2>
      <p class="text-sm m-0">Add a document to get started</p>
    </div>

    <template v-else>
      <!-- Grid view -->
      <template v-if="viewMode === 'grid'">
        <div v-if="annotatableDocs.length" data-testid="project-doc-grid" class="grid grid-cols-[repeat(auto-fill,minmax(275px,1fr))] gap-4">
          <div
            v-for="doc in annotatableDocs"
            :key="doc.id"
            data-testid="project-doc-card"
            class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden cursor-pointer hover:shadow-md dark:hover:border-slate-600 transition-all"
            @click="openDocument(doc)"
          >
            <div class="relative h-56 bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
              <img v-if="doc.preview" :src="doc.preview" class="h-40 w-auto max-w-[75%] object-contain shadow" />
              <button
                data-testid="project-doc-favorite"
                class="absolute top-4 left-4 flex items-center justify-center text-slate-400 hover:text-yellow-500 dark:hover:text-yellow-400 border-0 bg-transparent cursor-pointer transition-colors"
                @click.stop="toggleFavorite(doc)"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" :fill="doc.isFavorite ? 'currentColor' : 'none'" :class="doc.isFavorite ? 'text-yellow-400' : ''">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                </svg>
              </button>
              <span class="absolute bottom-0 left-0 px-3 py-1.5 rounded-tr-lg text-xs font-bold text-white uppercase" :class="mimeBadge(doc).class">{{ mimeBadge(doc).label }}</span>
            </div>
            <div class="p-4">
              <div class="flex items-start justify-between gap-2">
                <p :title="doc.name ?? 'Untitled'" class="text-base font-semibold text-slate-800 dark:text-slate-100 truncate m-0">{{ truncateMiddle(doc.name ?? "Untitled") }}</p>
                <DocumentCardMenu data-testid="project-doc-menu" v-if="project?.id" :document="doc" :project="project" @deleted="onDocumentDeleted(doc)" @renamed="onDocumentRenamed(doc, $event)" />
              </div>
              <p class="text-sm text-slate-400 dark:text-slate-500 m-0 mt-1 truncate">Last modified {{ formatDate(doc.updatedAt) }}</p>
            </div>
          </div>
        </div>

        <!-- Download-only files — can't be opened in the annotation viewer,
             stored/downloadable only (isAnnotatable: false), same concept as
             the legacy app's "Download-only Files" section. -->
        <div v-if="downloadOnlyDocs.length" data-testid="project-download-only-section" :class="annotatableDocs.length ? 'mt-8' : ''">
          <h2 class="text-base font-bold text-slate-700 dark:text-slate-200 m-0 mb-1">Download-only Files</h2>
          <p class="text-sm text-slate-400 dark:text-slate-500 m-0 mb-4">These files have been included as part of this project, but are available only to download.</p>
          <div data-testid="project-download-only-grid" class="grid grid-cols-[repeat(auto-fill,minmax(275px,1fr))] gap-4">
            <div
              v-for="doc in downloadOnlyDocs"
              :key="doc.id"
              data-testid="project-download-only-card"
              class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden cursor-pointer hover:shadow-md dark:hover:border-slate-600 transition-all"
              @click="openDocument(doc)"
            >
              <div class="relative h-56 bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                <img v-if="doc.preview" :src="doc.preview" class="h-40 w-auto max-w-[75%] object-contain shadow" />
                <button
                  data-testid="project-doc-favorite"
                  class="absolute top-4 left-4 flex items-center justify-center text-slate-400 hover:text-yellow-500 dark:hover:text-yellow-400 border-0 bg-transparent cursor-pointer transition-colors"
                  @click.stop="toggleFavorite(doc)"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" :fill="doc.isFavorite ? 'currentColor' : 'none'" :class="doc.isFavorite ? 'text-yellow-400' : ''">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                  </svg>
                </button>
                <span class="absolute bottom-0 left-0 px-3 py-1.5 rounded-tr-lg text-xs font-bold text-white uppercase" :class="mimeBadge(doc).class">{{ mimeBadge(doc).label }}</span>
              </div>
              <div class="p-4">
                <div class="flex items-start justify-between gap-2">
                  <p :title="doc.name ?? 'Untitled'" class="text-base font-semibold text-slate-800 dark:text-slate-100 truncate m-0">{{ truncateMiddle(doc.name ?? "Untitled") }}</p>
                  <DocumentCardMenu data-testid="project-doc-menu" v-if="project?.id" :document="doc" :project="project" @deleted="onDocumentDeleted(doc)" @renamed="onDocumentRenamed(doc, $event)" />
                </div>
                <p class="text-sm text-slate-400 dark:text-slate-500 m-0 mt-1 truncate">Last modified {{ formatDate(doc.updatedAt) }}</p>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- List view -->
      <template v-else>
        <table v-if="annotatableDocs.length" data-testid="project-doc-table" class="w-full table-fixed border-collapse bg-white dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
          <thead>
            <tr class="bg-slate-100 dark:bg-slate-700">
              <th class="w-[55%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Document name</th>
              <th class="w-[30%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Last modified</th>
              <th class="w-[15%] border-b border-slate-200 dark:border-slate-700" />
            </tr>
          </thead>
          <tbody class="align-top">
            <tr
              v-for="doc in annotatableDocs"
              :key="doc.id"
              data-testid="project-doc-row"
              class="border-b border-slate-100 dark:border-slate-700 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
              @click="openDocument(doc)"
            >
              <td class="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-100">
                <div class="flex items-start gap-2 min-w-0">
                  <button data-testid="project-doc-favorite" class="shrink-0 flex h-5 items-center justify-center text-slate-300 dark:text-slate-600 hover:text-yellow-500 dark:hover:text-yellow-400 border-0 bg-transparent cursor-pointer transition-colors" @click.stop="toggleFavorite(doc)">
                    <svg width="14" height="14" viewBox="0 0 24 24" :fill="doc.isFavorite ? 'currentColor' : 'none'" :class="doc.isFavorite ? 'text-yellow-400' : ''">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                    </svg>
                  </button>
                  <span class="min-w-0 [overflow-wrap:anywhere]">{{ doc.name ?? "Untitled" }}</span>
                </div>
              </td>
              <td class="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{{ formatDate(doc.updatedAt) }}</td>
              <td class="px-4 py-3 text-right" @click.stop>
                <DocumentCardMenu data-testid="project-doc-menu" v-if="project?.id" :document="doc" :project="project" @deleted="onDocumentDeleted(doc)" @renamed="onDocumentRenamed(doc, $event)" />
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Download-only files -->
        <div v-if="downloadOnlyDocs.length" data-testid="project-download-only-section" :class="annotatableDocs.length ? 'mt-8' : ''">
          <h2 class="text-base font-bold text-slate-700 dark:text-slate-200 m-0 mb-1">Download-only Files</h2>
          <p class="text-sm text-slate-400 dark:text-slate-500 m-0 mb-4">These files have been included as part of this project, but are available only to download.</p>
          <table data-testid="project-download-only-table" class="w-full table-fixed border-collapse bg-white dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
            <thead>
              <tr class="bg-slate-100 dark:bg-slate-700">
                <th class="w-[55%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Document name</th>
                <th class="w-[30%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Last modified</th>
                <th class="w-[15%] border-b border-slate-200 dark:border-slate-700" />
              </tr>
            </thead>
            <tbody class="align-top">
              <tr
                v-for="doc in downloadOnlyDocs"
                :key="doc.id"
                data-testid="project-download-only-row"
                class="border-b border-slate-100 dark:border-slate-700 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
                @click="openDocument(doc)"
              >
                <td class="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-100">
                  <div class="flex items-start gap-2 min-w-0">
                    <button data-testid="project-doc-favorite" class="shrink-0 flex h-5 items-center justify-center text-slate-300 dark:text-slate-600 hover:text-yellow-500 dark:hover:text-yellow-400 border-0 bg-transparent cursor-pointer transition-colors" @click.stop="toggleFavorite(doc)">
                      <svg width="14" height="14" viewBox="0 0 24 24" :fill="doc.isFavorite ? 'currentColor' : 'none'" :class="doc.isFavorite ? 'text-yellow-400' : ''">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                      </svg>
                    </button>
                    <span class="min-w-0 [overflow-wrap:anywhere]">{{ doc.name ?? "Untitled" }}</span>
                  </div>
                </td>
                <td class="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{{ formatDate(doc.updatedAt) }}</td>
                <td class="px-4 py-3 text-right" @click.stop>
                  <DocumentCardMenu data-testid="project-doc-menu" v-if="project?.id" :document="doc" :project="project" @deleted="onDocumentDeleted(doc)" @renamed="onDocumentRenamed(doc, $event)" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- Infinite scroll sentinel -->
      <div ref="sentinelEl" class="h-px" />
      <div v-if="isLoadingMore" class="flex justify-center py-6"><Spinner size="24px" /></div>
      <p v-else-if="!hasMore" class="text-center text-xs text-slate-400 dark:text-slate-500 py-6 m-0">You've reached the end</p>
    </template>

    <DownloadOnlyModal
      v-if="downloadOnlyDoc"
      :document="downloadOnlyDoc"
      :project="project"
      @close="downloadOnlyDoc = null"
    />
  </div>
</template>
